import type { EditableWithOverwrites } from "shared-types";

function isValidUrl(str: string, allowBareDomain: boolean): boolean {
    const input = str.trim();

    if (allowBareDomain) {
        const domainRegex = /^(?!:\/\/)([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/;
        if (domainRegex.test(input)) return true;
    }

    try {
        const url = new URL(input);
        const hasValidProtocol = ["http:", "https:"].includes(url.protocol);
        const hasValidTld = /\.[a-z]{2,}$/i.test(url.hostname);

        return hasValidProtocol && hasValidTld;
    } catch {
        return false;
    }
}

type ValidationType = "string" | "shortUrl" | "url" | "array";

const validatedFields: Record<string, { type: ValidationType }> = {
    fbCallToAction: { type: "array" },
    fbDescription: { type: "string" },
    fbDisplayLink: { type: "shortUrl" },
    fbHeadline: { type: "string" },
    fbPrimaryText: { type: "string" },
    fbWebsiteUrl: { type: "url" }
};

export function getInvalidMetaEditables(
    data: EditableWithOverwrites[],
    editableGroup: { label: string; value: string }
): { label: string; errors: { editableGroup: string; error: string }[] }[] {
    const errorsMap = new Map<string, string>();

    data.filter(
        ({ editable }) =>
            editable.publisherPlatform === "facebook" && Object.keys(validatedFields).includes(editable.name)
    ).forEach(({ editable, overwrites }) => {
        const { name } = editable;
        const validationType = validatedFields[name].type;

        const relevantOverwrites = overwrites.filter(
            o =>
                o.language === "en" &&
                Array.isArray(o.editableGroupValueIds) &&
                o.editableGroupValueIds.includes(editableGroup.value)
        );

        if (errorsMap.has(name)) {
            return;
        }

        if (relevantOverwrites.length === 0) {
            errorsMap.set(name, "has no value");
            return;
        }

        if (validationType === "string") {
            const hasValue = relevantOverwrites.some(o => typeof o.value === "string" && o.value.trim() !== "");
            if (!hasValue) {
                errorsMap.set(name, "has no value");
            }
        }

        if (validationType === "url") {
            const urlValue = relevantOverwrites.find(o => typeof o.value === "string")?.value;

            if (typeof urlValue !== "string" || urlValue.trim() === "" || !isValidUrl(urlValue, false)) {
                errorsMap.set(name, "is not a valid link");
            }
        }

        if (validationType === "shortUrl") {
            const urlValue = relevantOverwrites.find(o => typeof o.value === "string")?.value;

            if (typeof urlValue !== "string" || urlValue.trim() === "" || !isValidUrl(urlValue, true)) {
                errorsMap.set(name, "is not a valid display link");
            }
        }

        if (validationType === "array") {
            const hasSelected = relevantOverwrites.some(
                o => Array.isArray(o.value) && o.value.some(v => v.selected === true)
            );
            if (!hasSelected) {
                errorsMap.set(name, "has no value selected");
            }
        }
    });

    if (errorsMap.size === 0) {
        return [];
    }

    return [
        {
            label: editableGroup.label,
            errors: Array.from(errorsMap.entries()).map(([editableGroup, error]) => ({
                editableGroup,
                error
            }))
        }
    ];
}
